import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Search, Sparkles, ExternalLink, Building2, Users, Star, Calendar,
  ArrowRight, X, MapPin, RefreshCw, Loader2, CheckCircle2 as CheckCircle2Icon,
  Trash2,
} from "lucide-react";
import api, { formatErr } from "../lib/api";
import Modal from "../components/Modal";
import SearchableSelect from "../components/SearchableSelect";

/**
 * Deep Research index — the dedicated landing page for ALL Hidro deep
 * research reports. Lists every brief grouped by customer, with optional
 * lead/prospect binding. Top of the page hosts the "Run new research"
 * action which can target:
 *   - an existing lead (picker)
 *   - an existing customer
 *   - any free-text business name (Hidro will resolve via Google Places)
 *
 * Fixes the regression where the Sales-funnel "Deep research" tool card
 * incorrectly routed users to `/leads`.
 */
export default function DeepResearch() {
  const [briefs, setBriefs] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [leads, setLeads] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [runOpen, setRunOpen] = useState(false);
  const navigate = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const [b, c, l] = await Promise.all([
        api.get("/research/briefs").catch(() => ({ data: [] })),
        api.get("/customers").catch(() => ({ data: [] })),
        api.get("/leads").catch(() => ({ data: [] })),
      ]);
      setBriefs(Array.isArray(b.data) ? b.data : []);
      setCustomers(c.data || []);
      setLeads(l.data || []);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const term = (q || "").trim().toLowerCase();
    if (!term) return briefs;
    return briefs.filter((b) => {
      const hay = [
        b.prospect_name, b.lead_title, b.customer_name,
        b.industry, b.physical_address, b.phase,
      ].map((x) => (x ? String(x).toLowerCase() : "")).join(" | ");
      return hay.includes(term);
    });
  }, [briefs, q]);

  const grouped = useMemo(() => {
    // Group by customer_name when present, else "Uncustomered prospects"
    const groups = {};
    for (const b of filtered) {
      const key = b.customer_name || b.prospect_name || "Untitled";
      (groups[key] = groups[key] || []).push(b);
    }
    return Object.entries(groups).sort((a, b) => a[0].localeCompare(b[0]));
  }, [filtered]);

  return (
    <div className="space-y-5" data-testid="deep-research-page">
      {/* Hero */}
      <div className="relative overflow-hidden rounded-xl border border-white/10 px-5 py-4 md:px-6 md:py-5 bg-gradient-to-br from-[#EC4899]/10 via-[#0a0b0e] to-[#A78BFA]/10">
        <div className="relative flex items-center justify-between gap-4 flex-wrap">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#9aa3b5]">Sales intelligence</div>
            <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight mt-0.5 flex items-center gap-3">
              <Sparkles className="w-7 h-7 text-[#EC4899]" />
              Deep Research
            </h1>
            <p className="text-sm text-[#dde3ec] max-w-2xl mt-1">
              Every prospect 5-section AI brief Hidro has compiled — Google Places,
              website scrape, Wayback history, and internal call/comment context — sorted by customer.
            </p>
          </div>
          <button
            type="button"
            onClick={() => setRunOpen(true)}
            className="hv-btn hv-btn-primary"
            data-testid="deep-research-run-btn"
          >
            <Sparkles className="w-4 h-4" /> Run new research
          </button>
        </div>
      </div>

      {/* Search bar */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9aa3b5]" />
        <input
          type="search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search by prospect, customer, industry, location…"
          className="hv-input pl-9 py-2 text-sm w-full md:max-w-lg"
          data-testid="deep-research-search"
        />
      </div>

      {/* Reports list */}
      {loading ? (
        <div className="hv-card p-12 text-center text-[#dde3ec] flex items-center justify-center gap-2" data-testid="deep-research-loading">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading…
        </div>
      ) : grouped.length === 0 ? (
        <div className="hv-card p-12 text-center text-sm text-[#dde3ec]" data-testid="deep-research-empty">
          {q ? "No briefs match your search." : "No briefs yet — tap “Run new research” to compile your first one."}
        </div>
      ) : (
        <div className="space-y-3">
          {grouped.map(([groupName, items]) => (
            <div key={groupName} className="hv-card overflow-hidden" data-testid={`deep-research-group-${groupName}`}>
              <div className="px-4 py-3 border-b border-white/5 flex items-center justify-between bg-white/[0.02]">
                <div className="flex items-center gap-2">
                  <Building2 className="w-4 h-4 text-[#1FB6C1]" />
                  <h3 className="font-display text-sm font-bold">{groupName}</h3>
                  <span className="text-[10px] uppercase tracking-wider text-[#9aa3b5]">{items.length} brief{items.length === 1 ? "" : "s"}</span>
                </div>
              </div>
              <ul className="divide-y divide-white/5">
                {items.map((b) => (
                  <li key={b.lead_id || b.id}
                      className="px-4 py-3 hover:bg-[#EC4899]/5 transition group"
                      data-testid={`deep-research-row-${b.lead_id || b.id}`}>
                    <div className="flex items-start justify-between gap-3">
                      <button
                        type="button"
                        onClick={() => navigate(`/research/lead/${b.lead_id}`)}
                        className="min-w-0 flex-1 text-left"
                        data-testid={`deep-research-open-${b.lead_id}`}
                      >
                        <div className="text-sm font-medium text-[#f8f9fa]">{b.prospect_name || b.lead_title}</div>
                        <div className="text-xs text-[#dde3ec] mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5">
                          {b.industry && <span><Briefcase className="inline w-3 h-3 mr-1 text-[#A78BFA]" />{b.industry}</span>}
                          {b.physical_address && (
                            <span className="truncate max-w-xs">
                              <MapPin className="inline w-3 h-3 mr-1 text-[#10B981]" />{b.physical_address}
                            </span>
                          )}
                          {b.star_rating && (
                            <span><Star className="inline w-3 h-3 mr-1 text-amber-300" />{b.star_rating}</span>
                          )}
                          {b.hot_score != null && (
                            <span className="text-rose-300">heat° {b.hot_score}</span>
                          )}
                          {b.generated_at && (
                            <span className="text-[#9aa3b5]"><Calendar className="inline w-3 h-3 mr-1" />{new Date(b.generated_at).toLocaleDateString('en-GB')}</span>
                          )}
                        </div>
                      </button>
                      <div className="flex items-center gap-1 shrink-0 opacity-60 group-hover:opacity-100 transition">
                        <button
                          type="button"
                          onClick={async (e) => {
                            e.stopPropagation();
                            if (!window.confirm(`Delete the deep-research brief for "${b.prospect_name || b.lead_title}"?\n\nThis only removes the saved brief — the lead/customer record stays intact.`)) return;
                            try {
                              await api.delete(`/research/briefs/${b.lead_id}`);
                              setBriefs((curr) => curr.filter((x) => x.lead_id !== b.lead_id));
                            } catch (err) {
                              alert(formatErr(err));
                            }
                          }}
                          className="hv-btn hv-btn-ghost px-2 py-1"
                          title="Delete this brief"
                          data-testid={`deep-research-delete-${b.lead_id}`}
                        >
                          <Trash2 className="w-3.5 h-3.5 text-rose-300" />
                        </button>
                        <ExternalLink className="w-4 h-4 text-[#1FB6C1]" />
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}

      <RunResearchDialog
        open={runOpen}
        onClose={() => setRunOpen(false)}
        leads={leads}
        customers={customers}
        onCompiled={() => { setRunOpen(false); load(); }}
      />
    </div>
  );
}


// Minimal Briefcase icon shim for the row chip
function Briefcase(props) {
  return <span {...props} />;
}


// ─── Run new research dialog ──────────────────────────────────────────────────
//
// Single unified flow (per the user's "don't split screens" rule):
//   1. User types a business name OR picks an existing lead/customer (which
//      pre-fills the search box).
//   2. As they type, Google Places autocomplete shows live suggestions.
//   3. They MUST pick one Places result — confirms the business actually
//      exists on Google. If none of the suggestions are right, we offer a
//      "Verify" button that calls /research/verify and lists candidates.
//   4. Submit creates the lead (free) / binds the lead (existing) /
//      reuses the lead (lead picker), then runs the brief and routes to it.

function RunResearchDialog({ open, onClose, leads, customers, onCompiled }) {
  const [businessName, setBusinessName] = useState("");
  const [predictions, setPredictions] = useState([]);
  const [openDrop, setOpenDrop] = useState(false);
  const [predLoading, setPredLoading] = useState(false);
  const [confirmed, setConfirmed] = useState(null); // selected Places hit
  const [boundLeadId, setBoundLeadId] = useState("");      // existing lead pre-fill
  const [boundCustomerId, setBoundCustomerId] = useState(""); // existing customer pre-fill
  const [verifyChoices, setVerifyChoices] = useState([]); // /research/verify result list
  const [verifying, setVerifying] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const debounceRef = useRef(null);
  const wrapRef = useRef(null);
  const navigate = useNavigate();

  // Reset on open
  useEffect(() => {
    if (open) {
      setBusinessName("");
      setPredictions([]);
      setOpenDrop(false);
      setConfirmed(null);
      setBoundLeadId("");
      setBoundCustomerId("");
      setVerifyChoices([]);
      setErr("");
    }
  }, [open]);

  // Close dropdown on outside click
  useEffect(() => {
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpenDrop(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  // Autocomplete via /places/autocomplete (same endpoint PlaceInput uses)
  const fetchPredictions = (text) => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      if (!text || text.length < 2) { setPredictions([]); return; }
      setPredLoading(true);
      try {
        const { data } = await api.get("/places/autocomplete", {
          params: { q: text, country: "za" },
        });
        setPredictions(data.predictions || []);
      } catch (_e) {
        setPredictions([]);
      }
      setPredLoading(false);
    }, 250);
  };

  const onTypeName = (e) => {
    const t = e.target.value;
    setBusinessName(t);
    setConfirmed(null);
    setVerifyChoices([]);
    setOpenDrop(true);
    fetchPredictions(t);
  };

  const choosePlace = async (pred) => {
    setOpenDrop(false);
    setBusy(true);
    setErr("");
    try {
      const { data } = await api.get("/places/details", { params: { place_id: pred.place_id } });
      setConfirmed({
        name: data.name || pred.primary || pred.description || businessName,
        address: data.address,
        lat: data.lat,
        lng: data.lng,
        place_id: data.place_id || pred.place_id,
      });
      setBusinessName(data.name || pred.primary || pred.description || businessName);
    } catch (e) {
      setErr(formatErr(e?.response?.data?.detail, "Could not load place details."));
    } finally {
      setBusy(false);
    }
  };

  // Manual "Verify on Google" button — when autocomplete didn't surface
  // the right place. Pulls /research/verify and shows the candidate list.
  const verifyOnGoogle = async () => {
    if (!businessName.trim()) { setErr("Type a business name first."); return; }
    setVerifying(true);
    setErr("");
    setVerifyChoices([]);
    try {
      const { data } = await api.post("/research/verify", { name: businessName.trim() });
      if (!data?.ok) {
        setErr(`Google couldn't find "${businessName.trim()}". Try the address, region, or full legal name.`);
      } else {
        setVerifyChoices(data.candidates || []);
        setOpenDrop(false);
      }
    } catch (e) {
      setErr(formatErr(e?.response?.data?.detail, "Verify failed."));
    } finally {
      setVerifying(false);
    }
  };

  // Pre-fill the search box from an existing lead or customer pick.
  const pickLead = (lid) => {
    setBoundLeadId(lid);
    setBoundCustomerId("");
    const lead = leads.find((l) => l.id === lid);
    if (lead) {
      const seed = lead.title || lead.customer_name || "";
      setBusinessName(seed);
      setConfirmed(null);
      if (seed) fetchPredictions(seed);
      setOpenDrop(true);
    }
  };
  const pickCustomer = (cid) => {
    setBoundCustomerId(cid);
    setBoundLeadId("");
    const cust = customers.find((c) => c.id === cid);
    if (cust) {
      const seed = cust.name || "";
      setBusinessName(seed);
      setConfirmed(null);
      if (seed) fetchPredictions(seed);
      setOpenDrop(true);
    }
  };

  const canSubmit = !!confirmed && !busy;

  const submit = async () => {
    if (!confirmed) {
      setErr("Pick a Google Places result first so we know which business to research.");
      return;
    }
    setBusy(true);
    setErr("");
    try {
      // Existing-lead path: just (re)run /research/lead/{id} — the place is
      // already locked on the lead. We surface the place-confirm step
      // anyway so the user knows which entity Hidro will research.
      if (boundLeadId) {
        const { data } = await api.post(`/research/lead/${boundLeadId}`);
        if (onCompiled) await onCompiled(data);
        navigate(`/research/lead/${boundLeadId}`);
        return;
      }
      // Existing-customer path: bind/reuse lead, then research.
      if (boundCustomerId) {
        const { data } = await api.post("/research/customer", { customer_id: boundCustomerId });
        if (data?.lead_id) {
          if (onCompiled) await onCompiled(data);
          navigate(`/research/lead/${data.lead_id}`);
        } else {
          setErr("Couldn't bind a lead to that customer.");
        }
        return;
      }
      // Free-text path: now backed by a confirmed Places hit.
      const { data } = await api.post("/research/free", {
        name: confirmed.name,
        address: confirmed.address,
        place_id: confirmed.place_id,
        lat: confirmed.lat,
        lng: confirmed.lng,
      });
      if (data?.lead_id) {
        if (onCompiled) await onCompiled(data);
        navigate(`/research/lead/${data.lead_id}`);
      } else {
        setErr(data?.detail || "Couldn't compile a brief.");
      }
    } catch (e) {
      setErr(formatErr(e?.response?.data?.detail, "Research failed."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} title="Run new deep research" testId="deep-research-run-modal" maxWidth="max-w-lg">
      <div className="space-y-3">
        <div className="text-xs text-[#dde3ec]">
          Type a business name <em>or</em> pick from your existing leads/customers.
          Hidro will only research businesses Google can find — pick the matching
          Places result before compiling.
        </div>

        {/* Pre-fill from existing entity (collapsible) */}
        <details className="group" data-testid="research-prefill">
          <summary className="cursor-pointer text-[11px] uppercase tracking-[0.2em] text-[#9aa3b5] hover:text-white inline-flex items-center gap-1">
            <Building2 className="w-3 h-3" /> Pre-fill from existing lead or customer
          </summary>
          <div className="grid sm:grid-cols-2 gap-3 mt-3">
            <div>
              <label className="text-[10px] uppercase tracking-wider text-[#9aa3b5]">Lead</label>
              <SearchableSelect
                value={boundLeadId}
                onChange={pickLead}
                options={leads.map((l) => ({
                  id: l.id,
                  label: l.title || l.customer_name || l.id,
                  sublabel: [l.customer_name, l.phase, l.industry].filter(Boolean).join(" · "),
                  searchTerms: [l.title, l.customer_name, l.phase, l.industry, l.contact_name, l.physical_address].filter(Boolean),
                }))}
                placeholder="— pick a lead —"
                testId="research-prefill-lead"
              />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider text-[#9aa3b5]">Customer</label>
              <SearchableSelect
                value={boundCustomerId}
                onChange={pickCustomer}
                options={customers.map((c) => ({
                  id: c.id,
                  label: c.name,
                  sublabel: [c.external_ref && `Acc# ${c.external_ref}`, c.vat_number && `VAT ${c.vat_number}`, c.phone, c.email].filter(Boolean).join(" · "),
                  searchTerms: [c.name, c.external_ref, c.vat_number, c.phone, c.whatsapp, c.email, c.industry, c.physical_address, c.legal_name, c.short_id].filter(Boolean),
                }))}
                placeholder="— pick a customer —"
                testId="research-prefill-customer"
                emptyHint="No customers match. Try VAT, account no., phone or email."
              />
            </div>
          </div>
        </details>

        {/* Unified search box with autocomplete */}
        <div ref={wrapRef} className="relative" data-testid="research-search-wrap">
          <label className="text-[10px] uppercase tracking-wider text-[#9aa3b5]">
            Business name or address *
          </label>
          <div className="relative mt-1">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#9aa3b5] pointer-events-none" />
            <input
              type="text"
              value={businessName}
              onChange={onTypeName}
              onFocus={() => businessName.length >= 2 && setOpenDrop(true)}
              placeholder="e.g. Singita Lebombo Lodge, Sabi Sand"
              className="hv-input pl-8 text-sm w-full"
              data-testid="research-business-input"
              autoComplete="off"
            />
            {predLoading && (
              <Loader2 className="w-3.5 h-3.5 animate-spin absolute right-2.5 top-1/2 -translate-y-1/2 text-[#1FB6C1]" />
            )}
          </div>
          {/* Live autocomplete dropdown */}
          {openDrop && predictions.length > 0 && (
            <div className="absolute z-20 left-0 right-0 mt-1 bg-[#0a0b0e] border border-white/15 rounded-md shadow-2xl max-h-64 overflow-y-auto" data-testid="research-autocomplete">
              {predictions.map((p) => (
                <button
                  key={p.place_id || p.description}
                  type="button"
                  onClick={() => choosePlace(p)}
                  className="w-full text-left px-3 py-2 text-xs hover:bg-[#1FB6C1]/10 border-b border-white/5 last:border-b-0 flex items-start gap-2"
                  data-testid={`research-autocomplete-item-${p.place_id || ""}`}
                >
                  <MapPin className="w-3 h-3 text-[#1FB6C1] mt-0.5 shrink-0" />
                  <div className="min-w-0 flex-1">
                    <div className="text-white font-medium truncate">{p.primary || p.description}</div>
                    {p.secondary && <div className="text-[10px] text-[#9aa3b5] truncate">{p.secondary}</div>}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* "Verify on Google" fallback when autocomplete didn't show the right place */}
        <div className="flex items-center justify-between gap-2">
          <div className="text-[10px] text-[#9aa3b5]">
            Don't see the right business? Try the address or click Verify.
          </div>
          <button
            type="button"
            onClick={verifyOnGoogle}
            disabled={!businessName.trim() || verifying}
            className="hv-btn hv-btn-ghost text-[11px] px-2 py-1"
            data-testid="research-verify-btn"
          >
            {verifying ? <><RefreshCw className="w-3 h-3 animate-spin" /> Verifying…</> : <><Sparkles className="w-3 h-3 text-[#A78BFA]" /> Verify on Google</>}
          </button>
        </div>

        {/* Verify candidates */}
        {verifyChoices.length > 0 && !confirmed && (
          <div className="border border-[#A78BFA]/30 rounded-md p-2 space-y-1.5 bg-[#A78BFA]/5" data-testid="research-verify-results">
            <div className="text-[10px] uppercase tracking-wider text-[#A78BFA] font-bold">
              Pick the matching place
            </div>
            {verifyChoices.map((c) => (
              <button
                key={c.place_id}
                type="button"
                onClick={() => setConfirmed(c)}
                className="w-full text-left px-2 py-1.5 text-xs rounded hover:bg-[#A78BFA]/15 border border-transparent hover:border-[#A78BFA]/40 transition flex items-start gap-2"
                data-testid={`research-verify-pick-${c.place_id || ""}`}
              >
                <MapPin className="w-3 h-3 text-[#A78BFA] mt-0.5 shrink-0" />
                <div className="min-w-0 flex-1">
                  <div className="font-medium text-white truncate">{c.name}</div>
                  <div className="text-[10px] text-[#9aa3b5] truncate">{c.address}</div>
                  {(c.rating || c.user_ratings) && (
                    <div className="text-[10px] text-[#F59E0B]">
                      {c.rating ? `★ ${c.rating}` : ""}{c.user_ratings ? ` · ${c.user_ratings} reviews` : ""}
                    </div>
                  )}
                </div>
              </button>
            ))}
          </div>
        )}

        {/* Confirmed locked banner */}
        {confirmed && (
          <div className="border border-emerald-500/40 rounded-md p-3 bg-emerald-500/8 flex items-start gap-2" data-testid="research-confirmed">
            <CheckCircle2Icon className="w-4 h-4 text-emerald-400 mt-0.5 shrink-0" />
            <div className="min-w-0 flex-1">
              <div className="text-[10px] uppercase tracking-wider text-emerald-300 font-bold">Confirmed on Google</div>
              <div className="font-medium text-white">{confirmed.name}</div>
              {confirmed.address && (
                <div className="text-[11px] text-[#dde3ec] truncate">{confirmed.address}</div>
              )}
            </div>
            <button
              type="button"
              onClick={() => { setConfirmed(null); setVerifyChoices([]); }}
              className="text-[10px] text-[#9aa3b5] hover:text-rose-300 shrink-0"
              data-testid="research-confirmed-clear"
            >
              Change
            </button>
          </div>
        )}

        {err && (
          <div className="text-xs text-rose-300 border border-rose-500/30 bg-rose-500/10 rounded px-2 py-1.5" data-testid="research-run-err">{err}</div>
        )}

        <div className="flex justify-end gap-2 pt-2 border-t border-white/5">
          <button type="button" onClick={onClose} className="hv-btn hv-btn-ghost text-xs" data-testid="research-run-cancel">
            <X className="w-3.5 h-3.5" /> Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={!canSubmit}
            className={`hv-btn text-xs ${canSubmit ? "hv-btn-primary" : "hv-btn-ghost opacity-50 cursor-not-allowed"}`}
            data-testid="research-run-submit"
            title={canSubmit ? "Compile brief" : "Pick a Google Places result first"}
          >
            {busy ? <><RefreshCw className="w-3.5 h-3.5 animate-spin" /> Compiling…</> : <><Sparkles className="w-3.5 h-3.5" /> Compile brief</>}
          </button>
        </div>
      </div>
    </Modal>
  );
}
